<template>
    <FormulateForm v-model="formData" novalidate @submit="handleSubmit">
        <div class="form_field">
            <IconLock class="form_field_prefix" />
            <FormulateInput
                :type="!passwordVisibility.value ? 'password' : 'text'"
                name="password"
                placeholder="Password"
                validation="required|min:8,length|symbols|letters"
                :validation-rules="{
                    symbols: ({ value }) => /(?=.*[@$!%*#?&])/.test(value),
                    letters: ({ value }) => /(?=.*[A-Z])/.test(value),
                }"
                :validation-messages="{
                    required: 'Password is required.',
                    min: 'Must be at least 8 characters',
                    symbols: 'Symbols: !@#$%^&*',
                    letters: 'Uppercase letter: A-Z',
                }"
                :help="passwordStrength.value"
                @input="passwordStrength.check"
            >
                <template #help="{ help }">
                    <div
                        v-if="help"
                        :class="{
                            form_field_help: true,
                            form_field_help_weak: passwordStrength.value === 'weak',
                            form_field_help_medium: passwordStrength.value === 'medium',
                            form_field_help_strong: passwordStrength.value === 'strong',
                        }"
                    >
                        <IconPasswordShield />
                        <span>{{ help }}</span>
                    </div>
                </template>
            </FormulateInput>
            <button
                type="button"
                class="form_field_sufix"
                @click="passwordVisibility.toggle"
            >
                <IconEyeOpen v-if="passwordVisibility.value" />
                <IconEyeHide v-if="!passwordVisibility.value" />
            </button>
        </div>
        <div class="form_field">
            <IconLock class="form_field_prefix" />
            <FormulateInput
                :type="!passwordVisibility.value ? 'password' : 'text'"
                name="password_confirmation"
                placeholder="Confirm password"
                validation="required|matchPass"
                :validation-rules="{
                    matchPass: ({ value, getFormValues }) => value === getFormValues().password,
                }"
                :validation-messages="{
                    required: 'Password is required.',
                    matchPass: 'Confirmation does not match.',
                }"
            />
            <button
                type="button"
                class="form_field_sufix"
                @click="passwordVisibility.toggle"
            >
                <IconEyeOpen v-if="passwordVisibility.value" />
                <IconEyeHide v-if="!passwordVisibility.value" />
            </button>
        </div>

        <FormulateInput type="submit" label="Reset" class="submitButton" />
    </FormulateForm>
</template>

<script setup lang="ts">
import IconEmail from '@/components/icons/IconEmail.vue';
import IconLock from '@/components/icons/IconLock.vue';
import IconEyeHide from '@/components/icons/IconEyeHide.vue';
import IconEyeOpen from '@/components/icons/IconEyeOpen.vue';
import IconPasswordShield from '@/components/icons/IconPasswordShield.vue';
import { usePasswordStrength, usePasswordVisibility } from '../composables/passwords';
import type { Incomplete, ResetPasswordFormData } from '../types';
import { router } from '@inertiajs/vue2';
import route from 'ziggy-js';
import { ref } from 'vue';

const props = withDefaults(defineProps<{
    token?: string;
    email?: string;
}>(), {
    token: undefined,
    email: undefined,
});

const formData = ref<Incomplete<ResetPasswordFormData>>({
    email: props.email,
    token: props.token,
    password: null,
    password_confirmation: null,
});

function handleSubmit(data: ResetPasswordFormData) {
    router.post(route('password.update'), {
        ...data,
        token: props.token,
        email: props.email,
    });
}

const passwordVisibility = usePasswordVisibility();
const passwordStrength = usePasswordStrength();
</script>
